import { useState, useEffect, useCallback, useRef } from 'react';
import { PerformanceService } from '../services/PerformanceService';
import type { PerformanceFilters, PerformanceRow } from '../types/performance';
import type { PaginationInfo } from '../types/student';

const SEARCH_DEBOUNCE_MS = 400;

const areStringArraysEqual = (left?: string[], right?: string[]): boolean => {
  if (!left || !right) {
    return left === right;
  }

  return left.length === right.length
    && left.every((value, index) => value === right[index]);
};

export interface UsePerformanceReturn {
  rows: PerformanceRow[];
  pagination: PaginationInfo;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function usePerformanceData(
  page: number,
  pageSize: number,
  filters: PerformanceFilters = {},
): UsePerformanceReturn {
  const [rows, setRows] = useState<PerformanceRow[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page,
    pageSize,
    totalRecords: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const reqId = useRef(0);

  const fetchPage = useCallback(
    async (requestedPage: number, requestedPageSize: number, serverFilters: PerformanceFilters) => {
      const currentReq = ++reqId.current;

      try {
        setLoading(true);
        setError(null);

        const response = await PerformanceService.listPaginated(
          requestedPage,
          requestedPageSize,
          serverFilters,
        );

        if (currentReq !== reqId.current) {
          return;
        }

        setRows(response.data);
        setPagination(response.pagination);
      } catch (err: any) {
        if (currentReq !== reqId.current) {
          return;
        }

        console.error('[usePerformanceData]', err);
        setError(err.message || 'Erro ao carregar dados de performance');
      } finally {
        if (currentReq === reqId.current) {
          setLoading(false);
        }
      }
    },
    [],
  );

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const prevFilters = useRef(filters);
  const prevPage = useRef(page);
  const prevPageSize = useRef(pageSize);

  useEffect(() => {
    const onlySearchChanged =
      page === prevPage.current &&
      pageSize === prevPageSize.current &&
      filters.competence === prevFilters.current.competence &&
      filters.dateStart === prevFilters.current.dateStart &&
      filters.dateEnd === prevFilters.current.dateEnd &&
      filters.sortOrder === prevFilters.current.sortOrder &&
      areStringArraysEqual(filters.coachIds, prevFilters.current.coachIds) &&
      areStringArraysEqual(filters.statuses, prevFilters.current.statuses) &&
      areStringArraysEqual(filters.projectCodes, prevFilters.current.projectCodes) &&
      areStringArraysEqual(filters.classifications, prevFilters.current.classifications) &&
      filters.filterStudentTenure === prevFilters.current.filterStudentTenure &&
      filters.search !== prevFilters.current.search;

    prevFilters.current = filters;
    prevPage.current = page;
    prevPageSize.current = pageSize;

    if (onlySearchChanged) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        fetchPage(page, pageSize, filters);
      }, SEARCH_DEBOUNCE_MS);
      return () => clearTimeout(debounceTimer.current);
    }

    clearTimeout(debounceTimer.current);
    fetchPage(page, pageSize, filters);

    return () => clearTimeout(debounceTimer.current);
  }, [filters, fetchPage, page, pageSize]);

  const refetch = useCallback(() => {
    fetchPage(page, pageSize, filters);
  }, [fetchPage, filters, page, pageSize]);

  return { rows, pagination, loading, error, refetch };
}